// File: src/features/therapy/sessions-list-page.tsx
import {useEffect, useState} from "react";
import {Button, Card, Col, Container, Modal, Pagination, Row, Spinner, Stack} from "react-bootstrap";
import {authFetch} from "@/lib/auth-fetch";
import {useNavigate} from "react-router-dom";

const PAGE_SIZE = 30;

interface PagedResult<T> {
    items: T[];
    total: number;
    page: number;
    size: number;
}

interface SessionDTO {
    id: number;
    therapyType: string;
    date: string;
    time: string;
    therapistId?: number; // convenience duplicate of therapist
    therapist?: number; // id
    helper1?: number; // id
    helper2?: number; // id
    horse?: number; // id
    clients: number[]; // ids
    // display names
    therapistName?: string;
    helper1Name?: string;
    helper2Name?: string;
    horseName?: string;
    clientNames: string[];
    notes?: string;
    paidTherapist?: boolean;
}

export function SessionsListPage() {
    const [sessions, setSessions] = useState<SessionDTO[]>([]);
    const [loading, setLoading] = useState(true);
    const [me, setMe] = useState<{id:number; name:string; role:string} | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<SessionDTO | null>(null);
    const [page, setPage] = useState(0);
    const [total, setTotal] = useState(0);
    const [reloadKey, setReloadKey] = useState(0);
    const navigate = useNavigate();

    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const handleDelete = async () => {
        if (!deleteTarget) return;
        await authFetch(`/api/sessions/${deleteTarget.id}`, { method: "DELETE" });
        setDeleteTarget(null);
        // Reload current page so counts and fill-in from the next page stay correct
        setReloadKey(k => k + 1);
    };

    useEffect(() => {
        authFetch("/auth/me")
            .then(res => res.ok ? res.json() : null)
            .then(setMe);
    }, []);

    useEffect(() => {
        setLoading(true);
        authFetch(`/api/sessions?page=${page}&size=${PAGE_SIZE}`)
            .then(res => res.json())
            .then((data: PagedResult<SessionDTO>) => {
                setSessions(data.items);
                setTotal(data.total);
            })
            .finally(() => setLoading(false));
    }, [page, reloadKey]);

    // If deleting the last item on the last page leaves it empty, step back a page
    useEffect(() => {
        if (!loading && sessions.length === 0 && page > 0) {
            setPage(p => Math.max(0, p - 1));
        }
    }, [loading, sessions.length, page]);

    if (loading && sessions.length === 0) {
        return (
            <Container className="text-center mt-5">
                <Spinner animation="border" />
            </Container>
        );
    }

    return (
        <Container fluid>
            <Row className="mt-4">
                <Col xs={12}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h3 className="mb-0">Übersicht Sitzungen</h3>
                        <Button onClick={() => navigate("/sessions/new", { state: { from: "/sessions" } })}>Neue Sitzung</Button>
                    </div>

                    {sessions.length === 0 ? (
                        <Card>
                            <Card.Body>Keine Sitzungen vorhanden.</Card.Body>
                        </Card>
                    ) : (
                        <Row xs={1} md={2} xl={3} className="g-3">
                            {sessions.map((s) => {
                                const canEdit = me && me.role === "ADMIN";
                                const df = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });

                                return (
                                    <Col key={s.id}>
                                        <Card className="h-100">
                                            <Card.Body>
                                                {/* Header: date + time + type */}
                                                <div className="d-flex justify-content-between">
                                                    <div>
                                                        <div className="fw-semibold">
                                                            {df.format(new Date(s.date))} {s.time?.slice(0,5)}
                                                            {s.paidTherapist === false && (
                                                                <span className="badge bg-warning text-dark ms-2">unbezahlt</span>
                                                            )}
                                                        </div>
                                                        <div className="text-muted small">
                                                            {s.therapyType}
                                                        </div>
                                                    </div>
                                                </div>

                                                <hr />

                                                {/* Content fields */}
                                                <Stack gap={1}>
                                                    <div>
                                                        <span className="text-muted small">Klient(en): </span>
                                                        {s.clientNames?.length
                                                            ? s.clientNames.join(", ")
                                                            : "-"}
                                                    </div>

                                                    <div>
                                                        <span className="text-muted small">Fachkraft: </span>
                                                        {s.therapistName || "-"}
                                                    </div>

                                                    <div>
                                                        <span className="text-muted small">Helfer:</span>
                                                        {[s.helper1Name, s.helper2Name]
                                                            .filter(Boolean)
                                                            .join(", ") || "-"}
                                                    </div>

                                                    <div>
                                                        <span className="text-muted small">Pferd: </span>
                                                        {s.horseName || "-"}
                                                    </div>

                                                    {s.notes && s.notes.trim().length > 0 && (
                                                        <div>
                                                            <span className="text-muted small">Bemerkung: </span>
                                                            {s.notes}
                                                        </div>
                                                    )}
                                                </Stack>
                                            </Card.Body>

                                            {canEdit && (
                                                <Card.Footer className="d-flex justify-content-between">
                                                    <Button
                                                        size="sm"
                                                        variant="outline-danger"
                                                        onClick={() => setDeleteTarget(s)}
                                                    >
                                                        Löschen
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline-primary"
                                                        onClick={() => navigate(`/sessions/${s.id}/edit`, { state: { from: "/sessions" } })}
                                                    >
                                                        Bearbeiten
                                                    </Button>
                                                </Card.Footer>
                                            )}
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    )}

                    {total > PAGE_SIZE && (
                        <div className="d-flex justify-content-between align-items-center mt-3">
                            <span className="text-muted small">
                                {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} von {total}
                            </span>
                            <Pagination className="mb-0">
                                <Pagination.First disabled={page === 0} onClick={() => setPage(0)} />
                                <Pagination.Prev disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))} />
                                <Pagination.Item active>{page + 1} / {pageCount}</Pagination.Item>
                                <Pagination.Next disabled={page >= pageCount - 1} onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))} />
                                <Pagination.Last disabled={page >= pageCount - 1} onClick={() => setPage(pageCount - 1)} />
                            </Pagination>
                        </div>
                    )}
                </Col>
            </Row>
            <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Sitzung löschen</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {deleteTarget && (
                        <>
                            Sitzung vom <strong>{new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(deleteTarget.date))}</strong>
                            {" "}um <strong>{deleteTarget.time?.slice(0,5)}</strong>
                            {deleteTarget.therapistName && <> mit <strong>{deleteTarget.therapistName}</strong></>}
                            {" "}wirklich löschen?
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Abbrechen</Button>
                    <Button variant="danger" onClick={handleDelete}>Löschen</Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
}
